import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Spinner } from './Spinner';

/**
 * Tek buton kaynağı.
 *
 * Prototipte aynı 6-10 satırlık sınıf dizisi CalendarView, AdminPanel ve
 * TrainerPanel içinde ~30 kez tekrarlanıyordu. İkisi de eksikti:
 *   • focus-visible durumu → klavye kullanıcısı butonun nerede olduğunu görmüyordu
 *   • disabled durumu tutarsızdı (kimi yerde renk, kimi yerde cursor)
 */
/**
 * ## Hiyerarşi kararı
 *
 * Butonlar BÜYÜK HARF + geniş harf aralığıyla yazılıyordu. Bu, bir tanesi
 * ekranda olduğunda güçlü durur; her kartta bir tane olduğunda ekranı bağıran
 * eşit güçte sinyallere böler. Takvimde üç ders kartı = üç dolu mavi
 * "BOOK NOW" = kullanıcının gözü hiçbirini seçemiyordu.
 *
 * İki değişiklik:
 *   1. Cümle düzeni (normal case) + orta harf aralığı — okunur, sakin.
 *   2. `accent` artık dolu mavi DEĞİL, mürekkep. Mavi yalnızca SEÇİM ve odak
 *      için ayrıldı (aktif sekme, seçili yatak, focus halkası). Böylece
 *      "birincil eylem" ile "şu an neredeyim" farklı diller konuşur.
 *
 * Ölçekte de bir düzeltme var: tetikleyici buton (`accent`) ile onay modalinin
 * butonu farklı renklerdeydi — kullanıcı maviye basıp siyah bir onay görüyordu.
 * Artık ikisi de aynı.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-sharp font-semibold ' +
    'transition-colors select-none whitespace-nowrap ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'focus-visible:ring-accent focus-visible:ring-offset-canvas ' +
    'disabled:pointer-events-none disabled:opacity-45',
  {
    variants: {
      intent: {
        /* `action` rolü temaya göre TERSLENİR: aydınlıkta koyu buton,
           karanlıkta açık buton. Her iki temada da sayfadaki en yüksek
           kontrastlı öğe kalır — "birincil" olmanın görsel karşılığı budur. */
        primary: 'bg-action text-action-ink hover:bg-action-hover',
        accent: 'bg-action text-action-ink hover:bg-action-hover',
        /* Marka laciverti, iki temada da aynı (sitedeki "Panel Girişi"). */
        brand: 'bg-brand text-on-brand hover:bg-brand-hover',
        outline: 'border border-line-strong text-ink hover:border-ink hover:bg-sunken',
        waitlist: 'border border-warn bg-warn-soft text-warn hover:bg-warn-soft/70',
        danger: 'bg-risk text-canvas hover:bg-risk-hover',
        ghost: 'text-ink-2 hover:bg-sunken hover:text-ink',
        onDark: 'bg-white/12 text-shell-ink hover:bg-white/20',
      },
      /* Dokunma hedefi alt sınırları. Önceki ölçek `py-1 text-[10px]` ≈ 24px
         yükseklikteydi; parmakla güvenilir vurulamıyordu (Apple/Google önerisi
         44px, WCAG 2.5.8 asgari 24px). xs bile 36px'te taban yapar. */
      size: {
        xs: 'min-h-9 px-3 text-micro',
        sm: 'min-h-10 px-4 text-label',
        md: 'min-h-11 px-5 text-body',
        lg: 'min-h-12 px-6 text-body',
      },
      block: { true: 'w-full' },
    },
    defaultVariants: { intent: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  children: ReactNode;
}

export function Button({
  className,
  intent,
  size,
  block,
  isLoading = false,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(buttonVariants({ intent, size, block }), className)}
      disabled={disabled === true || isLoading}
      aria-busy={isLoading}
      {...rest}
    >
      {isLoading && <Spinner className="h-3.5 w-3.5" />}
      {children}
    </button>
  );
}
